/**
 * ヘッダーWeb Component
 * - ハンバーガーメニュー機能
 * - レスポンシブ対応
 * - アクセシビリティ対応
 * - 多言語対応
 */

// 翻訳定数オブジェクト
const HEADER_TRANSLATIONS = {
  ja: {
    about: 'VONSAIとは',
    findBonsai: '盆栽を探す',
    masters: '盆栽師を知る',
    investmentGuide: '盆栽所有ガイド',
    services: '提供サービス',
    logoText: '盆栽コレクション'
  },
  en: {
    about: 'About VONSAI',
    findBonsai: 'Find Bonsai',
    masters: 'Meet the Masters',
    investmentGuide: 'VONSAI Guide',
    services: 'Services',
    logoText: 'Bonsai Collection Platform'
  },
  'zh-cn': {
    about: '关于VONSAI',
    findBonsai: '寻找盆景',
    masters: '认识盆景师',
    investmentGuide: 'VONSAI指南',
    services: '服务',
    logoText: '盆景收藏平台'
  },
  'zh-tw': {
    about: '關於VONSAI',
    findBonsai: '尋找盆景',
    masters: '認識盆景師',
    investmentGuide: 'VONSAI指南',
    services: '服務',
    logoText: '盆景收藏平台'
  },
  ko: {
    about: 'VONSAI 소개',
    findBonsai: '분재 찾기',
    masters: '장인 만나기',
    investmentGuide: 'VONSAI 가이드',
    services: '서비스',
    logoText: '분재 컬렉션 플랫폼'
  },
  fr: {
    about: 'À propos de VONSAI',
    findBonsai: 'Trouver un bonsaï',
    masters: 'Rencontrer les maîtres',
    investmentGuide: 'Guide VONSAI',
    services: 'Services',
    logoText: 'Plateforme de collection bonsaï'
  },
  de: {
    about: 'Über VONSAI',
    findBonsai: 'Bonsai finden',
    masters: 'Meister kennenlernen',
    investmentGuide: 'VONSAI-Leitfaden',
    services: 'Dienstleistungen',
    logoText: 'Bonsai-Kollektionsplattform'
  },
  es: {
    about: 'Acerca de VONSAI',
    findBonsai: 'Encontrar bonsái',
    masters: 'Conocer a los maestros',
    investmentGuide: 'Guía VONSAI',
    services: 'Servicios',
    logoText: 'Plataforma de colección de bonsái'
  },
  ar: {
    about: 'حول VONSAI',
    findBonsai: 'البحث عن بونساي',
    masters: 'لقاء المعلمين',
    investmentGuide: 'دليل VONSAI',
    services: 'خدماتنا',
    logoText: 'منصة مجموعة البونساي'
  }
};

/**
 * 現在のロケールを検出
 * @returns {string} ロケールコード (例: 'ja', 'en', 'zh-cn')
 */
function detectLocale() {
  // 方法1: URLパスから検出 (例: /ja/pages/about, /en/pages/about)
  const pathMatch = window.location.pathname.match(/^\/([a-z]{2}(-[a-z]{2})?)\//i);
  if (pathMatch) {
    return pathMatch[1].toLowerCase();
  }

  // 方法2: <html lang="ja"> 属性から検出
  const htmlLang = document.documentElement.lang;
  if (htmlLang) {
    return htmlLang.toLowerCase();
  }

  // 方法3: クエリパラメータから検出 (例: ?locale=en)
  const urlParams = new URLSearchParams(window.location.search);
  const localeParam = urlParams.get('locale');
  if (localeParam) {
    return localeParam.toLowerCase();
  }

  // デフォルト: 日本語
  return 'ja';
}

/**
 * ローカライズされたURLを生成
 * @param {string} path - パス (例: '/pages/about')
 * @param {string} locale - ロケールコード
 * @returns {string} ローカライズされたURL
 */
function getLocalizedUrl(path, locale) {
  // locale が ja の場合はプレフィックスなし、それ以外は /en, /zh-cn などを追加
  if (locale === 'ja') {
    return `https://vonsai.tokyo${path}`;
  }
  return `https://vonsai.tokyo/${locale}${path}`;
}

class HeaderComponent extends HTMLElement {
  constructor() {
    super();
    this.isMenuOpen = false;
    // 言語を検出し、翻訳を選択
    this.locale = detectLocale();
    this.translations = HEADER_TRANSLATIONS[this.locale] || HEADER_TRANSLATIONS['ja'];
  }

  connectedCallback() {
    this.innerHTML = this.getTemplate();
    this.init();
  }

  getTemplate() {
    return `
      <div class="header-wrapper">
        <header class="header">
          <!-- ロゴ -->
          <a href="${getLocalizedUrl('/', this.locale)}" class="l-header__logo">
            <img src="https://cdn.shopify.com/s/files/1/0713/0821/4409/files/logo.svg?v=1754382576" alt="VONSAI ${this.translations.logoText}" width="158" height="47">
            <p class="l-header__logo__text">${this.translations.logoText}</p>
          </a>

          <!-- アイコン -->
          <div class="l-header__bar">
            <div class="l-header__bar__icons">
              <!-- カート -->
              <a href="${getLocalizedUrl('/cart', this.locale)}" class="header__icon header__icon--cart link focus-inset" id="cart-icon-bubble">
                <span class="svg-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                    <path d="M4.74935 4.99781H3.75666C2.69888 4.99781 2.16998 4.99781 1.79569 5.23392C1.46208 5.43504 1.20984 5.75859 1.07965 6.14335C0.925053 6.58057 1.01456 7.14022 1.18543 8.25952L1.94215 13.1565C2.07234 13.9872 2.13744 14.4069 2.33272 14.713C2.50359 14.9841 2.73956 15.2027 3.02435 15.3426C3.34168 15.5 3.73225 15.5 4.51338 15.5H11.4948C12.2759 15.5 12.6665 15.5 12.9838 15.3426C13.2604 15.2114 13.5045 14.9928 13.6754 14.713C13.8707 14.3982 13.9358 13.9872 14.0578 13.1565L14.8146 8.25952C14.9854 7.14022 15.0749 6.58057 14.9203 6.14335C14.7902 5.75859 14.5379 5.43504 14.2043 5.23392C13.83 4.99781 13.3011 4.99781 12.2433 4.99781H11.2507M4.74935 4.99781H11.2588C11.2588 3.06527 9.8023 1.5 8.00407 1.5C6.20584 1.5 4.74935 3.06527 4.74935 4.99781Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </a>

              <!-- お気に入り -->
              <a href="${getLocalizedUrl('/apps/wishlist', this.locale)}" class="l-header__bar__icons__wish">
                <span class="svg-wrapper">
                  <svg width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 2.86878C6.44444 -0.657404 1 -0.281478 1 4.22963C1 8.74074 8 12.5 8 12.5C8 12.5 15 8.74074 15 4.22963C15 -0.281478 9.55556 -0.657404 8 2.86878Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </span>
              </a>

              <!-- ログイン / マイページ -->
              <a href="${getLocalizedUrl('/account', this.locale)}">
                <span class="svg-wrapper">
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-account" viewBox="0 0 18 19">
                    <path fill="currentColor" fill-rule="evenodd" d="M6 4.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0m3-4a4 4 0 1 0 0 8 4 4 0 0 0 0-8m5.58 12.15c1.12.82 1.83 2.24 1.91 4.85H1.51c.08-2.6.79-4.03 1.9-4.85C4.66 11.75 6.5 11.5 9 11.5s4.35.26 5.58 1.15M9 10.5c-2.5 0-4.65.24-6.17 1.35C1.27 12.98.5 14.93.5 18v.5h17V18c0-3.07-.77-5.02-2.33-6.15-1.52-1.1-3.67-1.35-6.17-1.35" clip-rule="evenodd"/>
                  </svg>
                </span>
              </a>

              <!-- 検索 -->
              <a href="${getLocalizedUrl('/search', this.locale)}">
                <span class="svg-wrapper">
                  <svg fill="none" class="icon icon-search" viewBox="0 0 18 19">
                    <path fill="currentColor" fill-rule="evenodd" d="M11.03 11.68A5.784 5.784 0 1 1 2.85 3.5a5.784 5.784 0 0 1 8.18 8.18m.26 1.12a6.78 6.78 0 1 1 .72-.7l5.4 5.4a.5.5 0 1 1-.71.7z" clip-rule="evenodd"/>
                  </svg>
                </span>
              </a>
            </div>
            <!-- ハンバーガー -->
            <button type="button" class="l-header__bar__hamburger" id="hamburger-button">
              <span class="l-header__hamburger__dot" data-position="1"></span>
              <span class="l-header__hamburger__dot" data-position="2"></span>
              <span class="l-header__hamburger__dot" data-position="3"></span>
              <span class="l-header__hamburger__dot" data-position="4"></span>
              <span class="l-header__hamburger__dot" data-position="5"></span>
              <span class="l-header__hamburger__dot" data-position="6"></span>
              <span class="l-header__hamburger__dot" data-position="7"></span>
              <span class="l-header__hamburger__dot" data-position="8"></span>
              <span class="l-header__hamburger__dot" data-position="9"></span>
            </button>
          </div>

          <!-- ハンバーガーメニュー -->
          <div class="l-header__menu" id="hamburger-menu">
            <div class="l-header__menu__overlay"></div>
            <nav class="l-header__menu__content">
              <ul class="l-header__menu__list">
                <li class="l-header__menu__item">
                  <a href="${getLocalizedUrl('/pages/about', this.locale)}" class="l-header__menu__link">${this.translations.about}</a>
                </li>
                <li class="l-header__menu__item">
                  <a href="${getLocalizedUrl('/collections/all', this.locale)}" class="l-header__menu__link">${this.translations.findBonsai}</a>
                </li>
                <li class="l-header__menu__item">
                  <a href="${getLocalizedUrl('/pages/masters', this.locale)}" class="l-header__menu__link">${this.translations.masters}</a>
                </li>
                <li class="l-header__menu__item">
                  <a href="${getLocalizedUrl('/blogs/guide', this.locale)}" class="l-header__menu__link">${this.translations.investmentGuide}</a>
                </li>
                <li class="l-header__menu__item">
                  <a href="${getLocalizedUrl('/pages/own-log', this.locale)}" class="l-header__menu__link">${this.translations.services}</a>
                </li>
              </ul>
              <div class="l-header__menu__icons">
                <a href="${getLocalizedUrl('/cart', this.locale)}" class="l-header__menu__icon header__icon--cart">
                  <span class="svg-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="17" viewBox="0 0 16 17" fill="none">
                      <path d="M4.74935 4.99781H3.75666C2.69888 4.99781 2.16998 4.99781 1.79569 5.23392C1.46208 5.43504 1.20984 5.75859 1.07965 6.14335C0.925053 6.58057 1.01456 7.14022 1.18543 8.25952L1.94215 13.1565C2.07234 13.9872 2.13744 14.4069 2.33272 14.713C2.50359 14.9841 2.73956 15.2027 3.02435 15.3426C3.34168 15.5 3.73225 15.5 4.51338 15.5H11.4948C12.2759 15.5 12.6665 15.5 12.9838 15.3426C13.2604 15.2114 13.5045 14.9928 13.6754 14.713C13.8707 14.3982 13.9358 13.9872 14.0578 13.1565L14.8146 8.25952C14.9854 7.14022 15.0749 6.58057 14.9203 6.14335C14.7902 5.75859 14.5379 5.43504 14.2043 5.23392C13.83 4.99781 13.3011 4.99781 12.2433 4.99781H11.2507M4.74935 4.99781H11.2588C11.2588 3.06527 9.8023 1.5 8.00407 1.5C6.20584 1.5 4.74935 3.06527 4.74935 4.99781Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </a>
                <a href="${getLocalizedUrl('/pages/wishlist', this.locale)}" class="l-header__menu__icon">
                  <span class="svg-wrapper">
                    <svg width="16" height="13" viewBox="0 0 16 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 2.86878C6.44444 -0.657404 1 -0.281478 1 4.22963C1 8.74074 8 12.5 8 12.5C8 12.5 15 8.74074 15 4.22963C15 -0.281478 9.55556 -0.657404 8 2.86878Z" stroke="currentColor" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                  </span>
                </a>
                <a href="${getLocalizedUrl('/account', this.locale)}" class="l-header__menu__icon">
                  <span class="svg-wrapper">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" class="icon icon-account" viewBox="0 0 18 19">
                      <path fill="currentColor" fill-rule="evenodd" d="M6 4.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0m3-4a4 4 0 1 0 0 8 4 4 0 0 0 0-8m5.58 12.15c1.12.82 1.83 2.24 1.91 4.85H1.51c.08-2.6.79-4.03 1.9-4.85C4.66 11.75 6.5 11.5 9 11.5s4.35.26 5.58 1.15M9 10.5c-2.5 0-4.65.24-6.17 1.35C1.27 12.98.5 14.93.5 18v.5h17V18c0-3.07-.77-5.02-2.33-6.15-1.52-1.1-3.67-1.35-6.17-1.35" clip-rule="evenodd"/>
                    </svg>
                  </span>
                </a>
                <div class="l-header__menu__icon">
                  <a href="${getLocalizedUrl('/search', this.locale)}">
                    <span class="svg-wrapper">
                      <svg fill="none" class="icon icon-search" viewBox="0 0 18 19">
                        <path fill="currentColor" fill-rule="evenodd" d="M11.03 11.68A5.784 5.784 0 1 1 2.85 3.5a5.784 5.784 0 0 1 8.18 8.18m.26 1.12a6.78 6.78 0 1 1 .72-.7l5.4 5.4a.5.5 0 1 1-.71.7z" clip-rule="evenodd"/>
                      </svg>
                    </span>
                  </a>
                </div>
              </div>
            </nav>
          </div>
        </header>
      </div>
    `;
  }

  /**
   * 初期化処理
   */
  init() {
    this.hamburgerButton = this.querySelector('#hamburger-button');
    this.hamburgerMenu = this.querySelector('#hamburger-menu');
    this.menuOverlay = this.hamburgerMenu?.querySelector('.l-header__menu__overlay');

    if (!this.hamburgerButton || !this.hamburgerMenu) {
      console.warn('ハンバーガーメニューの要素が見つかりません');
      return;
    }

    this.bindEvents();
  }

  /**
   * イベントバインディング
   */
  bindEvents() {
    // ハンバーガーボタンクリック
    this.hamburgerButton.addEventListener('click', this.handleButtonClick.bind(this), true);

    // オーバーレイクリック（PC用）
    if (this.menuOverlay) {
      this.menuOverlay.addEventListener('click', this.closeMenu.bind(this));
    }

    // ESCキーで閉じる
    document.addEventListener('keydown', this.handleKeydown.bind(this));

    // リサイズ時の処理
    window.addEventListener('resize', this.handleResize.bind(this));
  }

  /**
   * ハンバーガーボタンクリック処理
   * @param {Event} e - イベントオブジェクト
   */
  handleButtonClick(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    this.toggleMenu();
  }

  /**
   * キーボードイベント処理
   * @param {KeyboardEvent} e - キーボードイベント
   */
  handleKeydown(e) {
    if (e.key === 'Escape' && this.isMenuOpen) {
      this.closeMenu();
    }
  }

  /**
   * リサイズイベント処理
   */
  handleResize() {
    if (this.isMenuOpen) {
      this.closeMenu();
    }
  }

  /**
   * メニューの開閉切り替え
   */
  toggleMenu() {
    if (this.isMenuOpen) {
      this.closeMenu();
    } else {
      this.openMenu();
    }
  }

  /**
   * メニューを開く
   */
  openMenu() {
    this.isMenuOpen = true;
    this.hamburgerMenu.classList.add('is-open');
    this.hamburgerButton.classList.add('is-open');
    // 初回アニメーション実行フラグを追加
    this.hamburgerButton.classList.add('has-animated');
    this.lockScroll();
  }

  /**
   * メニューを閉じる
   */
  closeMenu() {
    this.isMenuOpen = false;
    this.hamburgerMenu.classList.remove('is-open');
    this.hamburgerButton.classList.remove('is-open');
    this.unlockScroll();
  }

  /**
   * スクロールを無効化
   */
  lockScroll() {
    document.body.style.overflow = 'hidden';
  }

  /**
   * スクロールを有効化
   */
  unlockScroll() {
    document.body.style.overflow = '';
  }
}

// Web Componentを定義
customElements.define('vonsai-header', HeaderComponent);
